/**
 * TimeSheet Store
 *
 * Fokussierter Store für den TimeSheet Editor.
 * Nutzt Preact Signals für granulare Reaktivität.
 *
 * WICHTIG: Business-Logik (Pausenberechnung) kommt aus @timetrack/domain.
 * Dieser Store ist die Bruecke zwischen Domain und UI.
 *
 * PERSISTENZ: Alle Daten werden in IndexedDB (Dexie) gespeichert.
 * Nach Reload wird der Zustand wiederhergestellt.
 */

import { signal, computed } from '@preact/signals';
import { createId } from '@paralleldrive/cuid2';
import { DateTime } from 'luxon';
import type {
  TimeSheet,
  TimeEntry,
  TenantConfig,
  Employee,
  GeoPointWithTimestamp,
  RosterEntry,
  AbsenceReason,
  TimeEntryType,
  GeoPoint,
} from '@timetrack/domain';
import {
  calculateEntryNetDuration,
  calculateDurationStats,
  formatMinutesAsTime,
  minutesToDecimalHours,
  calculateStatutoryBreak,
  getMinutesBetween,
} from '@timetrack/domain';
import {
  db,
  getCachedEmployees,
  setCachedEmployees,
  getCachedTenantConfig,
  setCachedTenantConfig,
} from '../data/db';
import { showToast } from '../data/store';
import { SEED_IDS } from '@timetrack/db';
import { syncTimeSheet, fetchEmployees, fetchSyncStatus } from '../api/client';

// =============================================================================
// Types
// =============================================================================

export interface EntryWithStats extends TimeEntry {
  /** Berechnete Netto-Arbeitszeit in Minuten */
  netMinutes: number;
  /** Formatierte Netto-Zeit (HH:MM) */
  netTimeFormatted: string;
  /** Netto-Stunden dezimal */
  netHours: number;
}

export interface TimeSheetStats {
  totalEntries: number;
  totalGrossMinutes: number;
  totalBreakMinutes: number;
  totalNetMinutes: number;
  totalNetHours: number;
  totalNetFormatted: string;
}

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';

// =============================================================================
// Core State
// =============================================================================

/** Aktuelles TimeSheet */
export const currentSheet = signal<TimeSheet | null>(null);

/** Entries des aktuellen TimeSheets (raw) */
export const entries = signal<TimeEntry[]>([]);

/** Tenant-Konfiguration (für Pausenberechnung) */
export const config = signal<TenantConfig | null>(null);

/** Team-Mitarbeiter aus der DB */
export const teamMembers = signal<Employee[]>([]);

/** Ist der Store initialisiert? */
export const isInitialized = signal(false);

/** Wird gerade geladen? */
export const isLoading = signal(false);

/** Sync-Status mit Server */
export const syncStatus = signal<SyncStatus>('idle');

/** Letzter Sync-Fehler (für UI-Anzeige) */
export const lastSyncError = signal<string | null>(null);

/** Wird gerade GPS-Position ermittelt? */
export const isCapturingLocation = signal(false);

/** Nächster Auto-Retry Zeitpunkt (für UI Countdown) */
export const nextRetryAt = signal<number | null>(null);

/** Anzahl bisheriger Retry-Versuche */
export const retryCount = signal(0);

/** Roster-Auswahl (transient, nicht persistiert) */
export const rosterSelection = signal<Map<string, AbsenceReason>>(new Map());

// =============================================================================
// Derived State (mit Berechnungen)
// =============================================================================

/**
 * Entries mit berechneten Statistiken.
 * WICHTIG: Die Berechnung passiert hier, nicht in der View!
 */
export const entriesWithStats = computed<EntryWithStats[]>(() => {
  const cfg = config.value;
  if (!cfg) return [];

  return entries.value.map((entry) => {
    const netMinutes = calculateEntryNetDuration(entry, cfg);
    return {
      ...entry,
      netMinutes,
      netTimeFormatted: formatMinutesAsTime(netMinutes),
      netHours: minutesToDecimalHours(netMinutes),
    };
  });
});

/**
 * Gesamtstatistiken für das TimeSheet.
 */
export const sheetStats = computed<TimeSheetStats>(() => {
  const cfg = config.value;
  if (!cfg || entries.value.length === 0) {
    return {
      totalEntries: 0,
      totalGrossMinutes: 0,
      totalBreakMinutes: 0,
      totalNetMinutes: 0,
      totalNetHours: 0,
      totalNetFormatted: '00:00',
    };
  }

  const stats = calculateDurationStats(entries.value, cfg);

  return {
    totalEntries: stats.entryCount,
    totalGrossMinutes: stats.totalGrossMinutes,
    totalBreakMinutes: stats.totalBreakMinutes,
    totalNetMinutes: stats.totalNetMinutes,
    totalNetHours: minutesToDecimalHours(stats.totalNetMinutes),
    totalNetFormatted: formatMinutesAsTime(stats.totalNetMinutes),
  };
});

/**
 * Ist das TimeSheet im DRAFT-Status und bearbeitbar?
 */
export const isEditable = computed(() => {
  const sheet = currentSheet.value;
  if (!sheet) return false;
  return sheet.status === 'DRAFT' && !sheet.taLocked;
});

/**
 * Ist das TimeSheet gesperrt (TA durchgeführt)?
 */
export const isLocked = computed(() => {
  const sheet = currentSheet.value;
  if (!sheet) return false;
  return sheet.taLocked || sheet.status !== 'DRAFT';
});

/**
 * Status-Text für UI
 */
export const statusText = computed(() => {
  const sheet = currentSheet.value;
  if (!sheet) return '';

  if (sheet.taLocked) return 'Abgeschlossen';
  switch (sheet.status) {
    case 'DRAFT':
      return 'Entwurf';
    case 'SUBMITTED':
      return 'Eingereicht';
    case 'APPROVED':
      return 'Freigegeben';
    case 'EXPORTED':
      return 'Exportiert';
    default:
      return sheet.status;
  }
});

// =============================================================================
// Timer-bezogene Computed Signals
// =============================================================================

/**
 * Gibt es laufende Timer (status = RUNNING)?
 */
export const hasRunningEntries = computed(() =>
  entries.value.some((e) => e.status === 'RUNNING')
);

/**
 * Alle laufenden Entries (status = RUNNING).
 */
export const runningEntries = computed(() =>
  entries.value.filter((e) => e.status === 'RUNNING')
);

/**
 * Start-Zeitpunkt des ersten RUNNING entry (ISO-String).
 * Null wenn keine Timer laufen.
 */
export const workdayStartTime = computed(() => {
  const running = entries.value.find((e) => e.status === 'RUNNING');
  return running?.startTime ?? null;
});

/**
 * Anzahl anwesender Mitarbeiter (WORK entries mit RUNNING status).
 */
export const presentCount = computed(
  () => entries.value.filter((e) => e.status === 'RUNNING' && e.type === 'WORK').length
);

// =============================================================================
// Helper Functions
// =============================================================================

function getTodayDateString(): string {
  const iso = new Date().toISOString();
  return iso.split('T')[0] ?? iso.substring(0, 10);
}

function getNowTimestamp(): string {
  return DateTime.now().toUTC().toISO();
}

/**
 * Gibt aktuellen Zeitpunkt ohne Sekunden zurück (auf Minute gerundet).
 * Für startTime/endTime von TimeEntries - Sekunden sind Noise für Zeiterfassung.
 * WICHTIG: Muss UTC mit 'Z' Suffix sein für Contract-Validierung.
 */
function getNowTimeForEntry(): string {
  return DateTime.now().toUTC().startOf('minute').toISO();
}

/** GPS Timeout in ms */
const GPS_TIMEOUT_MS = 5000;

/** Retry-Konfiguration (Exponential Backoff) */
const RETRY_BASE_MS = 1000;      // Start: 1s
const RETRY_MAX_MS = 30000;      // Max: 30s
const RETRY_MAX_ATTEMPTS = 10;   // Nach 10 Versuchen aufgeben

/** Timer-ID für Auto-Retry */
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Erfasst GPS-Position mit Timeout.
 * Gibt null zurueck wenn:
 * - GPS nicht verfügbar
 * - User lehnt ab
 * - Timeout überschritten
 *
 * Blockiert NICHT den Abschluss!
 */
async function captureLocation(): Promise<GeoPointWithTimestamp | null> {
  // Prüfen ob Geolocation API verfügbar
  if (!('geolocation' in navigator)) {
    console.warn('[GPS] Geolocation API nicht verfügbar');
    return null;
  }

  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn('[GPS] Timeout nach', GPS_TIMEOUT_MS, 'ms');
      resolve(null);
    }, GPS_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        const location: GeoPointWithTimestamp = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };
        console.info('[GPS] Position erfasst:', location);
        resolve(location);
      },
      (error) => {
        clearTimeout(timeoutId);
        console.warn('[GPS] Fehler:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false, // schneller, reicht für Baustellen-Check
        timeout: GPS_TIMEOUT_MS,
        maximumAge: 60000, // 1 Minute alte Position ist ok
      }
    );
  });
}

// =============================================================================
// Initialization
// =============================================================================

/** Aktuell geladenes Datum (fuer Re-Init bei Datumswechsel) */
let currentLoadedDate: string | null = null;

/**
 * Initialisiert den Store.
 *
 * @param date - Datum im Format YYYY-MM-DD. Wenn nicht angegeben: heute.
 *
 * 1. Lade Employees + TenantConfig aus LocalStorage-Cache (Offline-Fallback)
 * 2. Hole Employees + TenantConfig vom Server (aktualisiert Cache)
 * 3. Suche TimeSheet fuer das angegebene Datum in Dexie
 *    - Falls vorhanden: Laden
 *    - Falls nicht: Neues erstellen
 * 4. Check Remote Status (Pull-Sync fuer Rejection Loop)
 */
export async function initStore(date?: string): Promise<void> {
  // Ziel-Datum bestimmen
  const targetDate = date ?? getTodayDateString();

  // Pruefen ob bereits fuer dieses Datum initialisiert
  if (isInitialized.value && currentLoadedDate === targetDate) {
    console.info('[Store] Bereits fuer', targetDate, 'initialisiert');
    return;
  }

  // Bei Datumswechsel: Re-Init
  if (isInitialized.value && currentLoadedDate !== targetDate) {
    console.info('[Store] Datumswechsel von', currentLoadedDate, 'zu', targetDate);
    isInitialized.value = false;
  }

  isLoading.value = true;
  console.info('[Store] Initialisiere fuer Datum:', targetDate);

  try {
    // 1. Aus Dexie-Cache laden (Offline-Fallback)
    const [cachedEmployees, cachedConfig] = await Promise.all([
      getCachedEmployees(),
      getCachedTenantConfig(),
    ]);

    if (cachedEmployees.length > 0) {
      teamMembers.value = cachedEmployees.filter((e) => e.active);
      console.info('[Store] Employees aus Cache geladen:', teamMembers.value.length);
    }

    if (cachedConfig) {
      config.value = cachedConfig;
      console.info('[Store] TenantConfig aus Cache geladen:', cachedConfig.name);
    }

    // 2. Vom Server holen (aktualisiert Cache + State)
    // Non-blocking - UI zeigt erstmal Cache-Daten
    syncStammdatenFromServer().catch((err) => {
      console.warn('[Store] Stammdaten-Sync fehlgeschlagen:', err);
      if (teamMembers.value.length === 0) {
        showToast('error', 'Keine Mitarbeiter verfügbar - Server nicht erreichbar');
      }
    });

    // 3. TimeSheet fuer das Ziel-Datum suchen
    const existingSheet = await db.timeSheet
      .where('[tenantId+date]')
      .equals([SEED_IDS.DEMO_TENANT, targetDate])
      .first();

    if (existingSheet) {
      // Existierendes Sheet laden (auch wenn locked/submitted)
      console.info('[Store] TimeSheet gefunden:', existingSheet.id, 'Status:', existingSheet.status, 'Locked:', existingSheet.taLocked);
      await loadSheet(existingSheet.id);
    } else {
      // Kein Sheet fuer dieses Datum -> neues erstellen
      console.info('[Store] Kein TimeSheet fuer', targetDate, 'gefunden, erstelle neues');
      await createSheetForDate(targetDate);
    }

    // 4. Check Remote Status (Pull-Sync fuer Rejection Loop)
    // Nur wenn online und ein Sheet geladen ist
    checkRemoteStatus().catch((err) => {
      console.warn('[Store] Remote-Status-Check fehlgeschlagen:', err);
    });

    currentLoadedDate = targetDate;
    isInitialized.value = true;
    console.info('[Store] Initialisierung abgeschlossen fuer:', targetDate);
  } catch (error) {
    console.error('[Store] Initialisierung fehlgeschlagen:', error);
    showToast('error', 'Fehler beim Laden der Daten');
  } finally {
    isLoading.value = false;
  }
}

/**
 * Synchronisiert Stammdaten (Employees + TenantConfig) vom Server.
 * Aktualisiert Dexie-Cache und Signal-State.
 */
async function syncStammdatenFromServer(): Promise<void> {
  console.info('[Store] Starte Stammdaten-Sync...');

  // Employees vom Server holen
  const serverEmployees = await fetchEmployees();

  // null = Fehler/Offline -> Cache behalten
  // [] = Server antwortet mit leerer Liste -> Cache leeren
  if (serverEmployees !== null) {
    // Konvertiere Server-Format zu Employee-Format
    const employees: Employee[] = serverEmployees.map((emp) => ({
      id: emp.id,
      tenantId: SEED_IDS.DEMO_TENANT,
      personnelNumber: emp.personnelNumber,
      firstName: emp.firstName,
      lastName: emp.lastName,
      foremanId: emp.foremanId ?? SEED_IDS.DEMO_FOREMAN,
      active: emp.active,
      createdAt: emp.createdAt,
      updatedAt: emp.updatedAt,
    }));

    // In Dexie cachen (auch leere Liste!)
    await setCachedEmployees(employees);

    // State aktualisieren
    const activeEmployees = employees.filter((e) => e.active);
    teamMembers.value = activeEmployees;

    console.info('[Store] Employees-Sync erfolgreich:', activeEmployees.length, 'aktive Mitarbeiter');
  } else {
    console.info('[Store] Employees-Fetch fehlgeschlagen (offline?) - Cache behalten');
  }

  // TODO: TenantConfig vom Server holen wenn API vorhanden
  // Fuer jetzt: Default-Config wenn keine gecached ist
  if (!config.value) {
    const defaultConfig: TenantConfig = {
      id: SEED_IDS.DEMO_TENANT,
      name: 'TimeTrack',
      theme: { primaryColor: 'blue' },
      breakRule: { type: 'STATUTORY' },
      datevConfig: { wageTypeHours: 1000 },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    config.value = defaultConfig;
    await setCachedTenantConfig(defaultConfig);
    console.info('[Store] Default-TenantConfig gesetzt');
  }
}

/**
 * Prüft den Remote-Status von TimeSheets.
 * Wenn ein Sheet auf dem Server zurueckgewiesen wurde (DRAFT),
 * wird es lokal entsperrt.
 */
export async function checkRemoteStatus(): Promise<void> {
  const sheet = currentSheet.value;

  if (!sheet) {
    console.info('[Store] checkRemoteStatus: Kein Sheet geladen');
    return;
  }

  console.info('[Store] Prüfe Remote-Status...');

  const response = await fetchSyncStatus(SEED_IDS.DEMO_FOREMAN);

  if (!response) {
    console.info('[Store] Remote-Status nicht verfügbar (offline?)');
    return;
  }

  // Finde das aktuelle Sheet in der Server-Antwort
  const serverSheet = response.sheets.find((s) => s.id === sheet.id);

  if (!serverSheet) {
    console.info('[Store] Sheet nicht auf Server gefunden (noch nicht synchronisiert?)');
    return;
  }

  console.info('[Store] Server-Status:', serverSheet.status, 'Lokal:', sheet.status);

  // Prüfe ob Server-Status anders ist als lokal
  // Wichtigster Fall: Server sagt DRAFT, lokal ist SUBMITTED/locked
  if (serverSheet.status === 'DRAFT' && (sheet.status === 'SUBMITTED' || sheet.taLocked)) {
    console.info('[Store] Sheet wurde auf Server zurueckgewiesen! Unlock lokal...');

    const now = getNowTimestamp();

    const unlocked: TimeSheet = {
      ...sheet,
      status: 'DRAFT',
      taLocked: false,
      approvedBy: undefined,
      approvedAt: undefined,
      updatedAt: now,
      version: Math.max(sheet.version, serverSheet.version),
    };

    // In DB speichern
    await db.timeSheet.put(unlocked);

    // State aktualisieren
    currentSheet.value = unlocked;
    syncStatus.value = 'idle';
    lastSyncError.value = null;
    cancelAutoRetry();

    // User informieren
    showToast('warning', `Zeitmeldung vom ${sheet.date} wurde zurückgewiesen. Bitte korrigieren.`);
  } else if (serverSheet.status === 'APPROVED' && sheet.status !== 'APPROVED') {
    // Sheet wurde freigegeben - Status lokal aktualisieren
    console.info('[Store] Sheet wurde auf Server freigegeben.');

    const now = getNowTimestamp();

    const approved: TimeSheet = {
      ...sheet,
      status: 'APPROVED',
      updatedAt: now,
      version: Math.max(sheet.version, serverSheet.version),
    };

    await db.timeSheet.put(approved);
    currentSheet.value = approved;
  }
}

/**
 * Lädt ein TimeSheet mit allen Entries aus der DB.
 *
 * WICHTIG: Keine Deduplizierung mehr - Multi-Session Support!
 * Pro Mitarbeiter koennen mehrere Entries existieren (morgens, nachmittags, etc.)
 */
export async function loadSheet(sheetId: string): Promise<boolean> {
  const sheet = await db.timeSheet.get(sheetId);

  if (!sheet) {
    showToast('error', 'TimeSheet nicht gefunden');
    return false;
  }

  const sheetEntries = await db.timeEntry
    .where('timeSheetId')
    .equals(sheetId)
    .toArray();

  // Sortiere nach startTime (älteste zuerst)
  sheetEntries.sort((a, b) => a.startTime.localeCompare(b.startTime));

  currentSheet.value = sheet;
  entries.value = sheetEntries;

  console.info('[Store] TimeSheet geladen:', sheetId, 'mit', sheetEntries.length, 'Entries');
  return true;
}

/**
 * Erstellt ein neues TimeSheet fuer ein bestimmtes Datum.
 *
 * @param date - Datum im Format YYYY-MM-DD
 */
export async function createSheetForDate(date: string): Promise<TimeSheet> {
  const now = getNowTimestamp();

  const sheet: TimeSheet = {
    id: createId(),
    tenantId: SEED_IDS.DEMO_TENANT,
    date,
    foremanId: SEED_IDS.DEMO_FOREMAN,
    supervisorId: 'supervisor-demo',
    costCenterId: 'cost-center-demo',
    status: 'DRAFT',
    taLocked: false,
    createdAt: now,
    updatedAt: now,
    version: 1,
  };

  // In DB speichern
  await db.timeSheet.add(sheet);

  // State aktualisieren
  currentSheet.value = sheet;
  entries.value = [];

  console.info('[Store] Neues TimeSheet erstellt fuer:', date, 'ID:', sheet.id);
  return sheet;
}

/**
 * Erstellt ein neues TimeSheet fuer heute.
 * @deprecated Verwende createSheetForDate(getTodayDateString()) stattdessen
 */
export async function createTodaySheet(): Promise<TimeSheet> {
  return createSheetForDate(getTodayDateString());
}

// =============================================================================
// Actions
// =============================================================================

/**
 * Fügt einen neuen TimeEntry hinzu.
 * Speichert sofort in die DB.
 */
export async function addEntry(
  employeeId: string,
  start: Date,
  end: Date,
  employeeName: string,
  personnelNumber: string,
  breakMinutes = 0
): Promise<TimeEntry | null> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return null;
  }

  if (!isEditable.value) {
    showToast('error', 'Zeitmeldung ist bereits abgeschlossen');
    return null;
  }

  // Prüfe ob Mitarbeiter bereits erfasst ist (verhindert Duplikate)
  const alreadyExists = entries.value.some((e) => e.employeeId === employeeId);
  if (alreadyExists) {
    console.warn('[Store] Mitarbeiter bereits erfasst:', employeeId);
    return null;
  }

  const now = getNowTimestamp();

  const entry: TimeEntry = {
    id: createId(),
    timeSheetId: sheet.id,
    employeeId,
    personnelNumber,
    employeeName,
    type: 'WORK',
    status: 'COMPLETED',
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    breakMinutes,
    createdAt: now,
    updatedAt: now,
    version: 1,
  };

  // 1. Entry in DB speichern
  await db.timeEntry.add(entry);

  // 2. TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // 3. State aktualisieren
  entries.value = [...entries.value, entry];
  currentSheet.value = updatedSheet;

  showToast('success', `${employeeName} hinzugefügt`);
  return entry;
}

/**
 * Aktualisiert einen bestehenden TimeEntry.
 * Speichert sofort in die DB.
 */
export async function updateEntry(
  entryId: string,
  updates: { startTime?: Date; endTime?: Date; breakMinutes?: number }
): Promise<boolean> {
  if (!isEditable.value) {
    showToast('error', 'Zeitmeldung ist bereits abgeschlossen');
    return false;
  }

  const entry = entries.value.find((e) => e.id === entryId);
  if (!entry) {
    showToast('error', 'Eintrag nicht gefunden');
    return false;
  }

  const sheet = currentSheet.value;
  if (!sheet) return false;

  const now = getNowTimestamp();

  const updatedEntry: TimeEntry = {
    ...entry,
    startTime: updates.startTime ? updates.startTime.toISOString() : entry.startTime,
    endTime: updates.endTime ? updates.endTime.toISOString() : entry.endTime,
    breakMinutes: updates.breakMinutes ?? entry.breakMinutes,
    updatedAt: now,
    version: entry.version + 1,
  };

  // 1. Entry in DB aktualisieren
  await db.timeEntry.put(updatedEntry);

  // 2. TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // 3. State aktualisieren
  entries.value = entries.value.map((e) => (e.id === entryId ? updatedEntry : e));
  currentSheet.value = updatedSheet;

  return true;
}

/**
 * Entfernt einen TimeEntry.
 * Löscht sofort aus der DB.
 */
export async function removeEntry(entryId: string): Promise<boolean> {
  if (!isEditable.value) {
    showToast('error', 'Zeitmeldung ist bereits abgeschlossen');
    return false;
  }

  const entry = entries.value.find((e) => e.id === entryId);
  if (!entry) return false;

  const sheet = currentSheet.value;
  if (!sheet) return false;

  const now = getNowTimestamp();

  // 1. Entry aus DB löschen
  await db.timeEntry.delete(entryId);

  // 2. TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // 3. State aktualisieren
  entries.value = entries.value.filter((e) => e.id !== entryId);
  currentSheet.value = updatedSheet;

  showToast('info', `${entry.employeeName} entfernt`);
  return true;
}

/**
 * Fuehrt den Tagesabschluss durch (TA).
 *
 * Ablauf:
 * 1. GPS-Position erfassen (non-blocking, 5s Timeout)
 * 2. TimeSheet locken und in DB speichern
 * 3. Sync mit Server triggern (async, im Hintergrund)
 */
export async function lockDay(): Promise<boolean> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return false;
  }

  if (sheet.taLocked) {
    showToast('warning', 'Bereits abgeschlossen');
    return false;
  }

  if (entries.value.length === 0) {
    showToast('error', 'Keine Einträge vorhanden');
    return false;
  }

  // 1. GPS-Position erfassen (non-blocking)
  isCapturingLocation.value = true;
  let location: GeoPointWithTimestamp | null = null;
  try {
    location = await captureLocation();
  } finally {
    isCapturingLocation.value = false;
  }

  const now = getNowTimestamp();

  // 2. TimeSheet locken
  const updated: TimeSheet = {
    ...sheet,
    taLocked: true,
    status: 'SUBMITTED',
    submissionLocation: location ?? undefined,
    updatedAt: now,
    version: sheet.version + 1,
  };

  // In DB speichern
  await db.timeSheet.put(updated);

  // State aktualisieren
  currentSheet.value = updated;

  showToast('success', 'Tagesabschluss erfolgreich');

  // 3. Sync im Hintergrund triggern (fire-and-forget)
  void performSync(updated);

  return true;
}

/**
 * Berechnet Backoff-Delay: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...
 */
function calculateBackoffDelay(attempt: number): number {
  const delay = RETRY_BASE_MS * Math.pow(2, attempt);
  return Math.min(delay, RETRY_MAX_MS);
}

/**
 * Stoppt laufenden Auto-Retry Timer.
 */
function cancelAutoRetry(): void {
  if (retryTimeoutId !== null) {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
    nextRetryAt.value = null;
    console.info('[Sync] Auto-Retry abgebrochen');
  }
}

/**
 * Plant Auto-Retry mit Exponential Backoff.
 */
function scheduleAutoRetry(sheet: TimeSheet): void {
  const attempt = retryCount.value;

  if (attempt >= RETRY_MAX_ATTEMPTS) {
    console.warn('[Sync] Max Retry-Versuche erreicht:', RETRY_MAX_ATTEMPTS);
    showToast('error', 'Sync fehlgeschlagen - bitte manuell erneut versuchen');
    return;
  }

  const delay = calculateBackoffDelay(attempt);
  const retryTime = Date.now() + delay;
  nextRetryAt.value = retryTime;

  console.info(`[Sync] Auto-Retry #${attempt + 1} in ${delay / 1000}s`);

  retryTimeoutId = setTimeout(() => {
    retryTimeoutId = null;
    nextRetryAt.value = null;
    retryCount.value += 1;
    void performSync(sheet);
  }, delay);
}

/**
 * Fuehrt den Sync mit dem Server durch.
 * Bei Fehler: Auto-Retry mit Exponential Backoff.
 */
async function performSync(sheet: TimeSheet): Promise<void> {
  console.info('[Sync] Starte Sync für Sheet:', sheet.id, 'Versuch:', retryCount.value + 1);

  syncStatus.value = 'syncing';
  lastSyncError.value = null;
  nextRetryAt.value = null;

  try {
    // Sync mit den aktuellen Entries aus dem Store
    const result = await syncTimeSheet(sheet, entries.value);

    if (result.success) {
      syncStatus.value = 'synced';
      retryCount.value = 0; // Reset bei Erfolg
      console.info('[Sync] Erfolgreich! Synced at:', result.syncedAt);
    } else {
      syncStatus.value = 'error';
      lastSyncError.value = result.error ?? 'Unbekannter Fehler';
      console.error('[Sync] Fehler:', result.error);
      scheduleAutoRetry(sheet);
    }
  } catch (error) {
    syncStatus.value = 'error';
    const message = error instanceof Error ? error.message : 'Netzwerkfehler';
    lastSyncError.value = message;
    console.error('[Sync] Exception:', error);
    scheduleAutoRetry(sheet);
  }
}

/**
 * Versucht den Sync erneut (manuell).
 * Bricht laufenden Auto-Retry ab und startet sofort neu.
 */
export async function retrySync(): Promise<void> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return;
  }

  if (!sheet.taLocked) {
    showToast('warning', 'TimeSheet ist noch nicht abgeschlossen');
    return;
  }

  if (syncStatus.value === 'syncing') {
    showToast('info', 'Sync läuft bereits...');
    return;
  }

  // Manueller Retry: Timer abbrechen, Counter zuruecksetzen
  cancelAutoRetry();
  retryCount.value = 0;

  await performSync(sheet);
}

// =============================================================================
// Timer Actions (Start/Stop Day)
// =============================================================================

/**
 * Startet den Arbeitstag fuer alle ausgewaehlten Mitarbeiter.
 *
 * @param roster - Liste der Mitarbeiter mit ihrem Anwesenheits-Status
 *
 * - 'present': Erstellt WORK Entry mit status=RUNNING
 * - 'sick': Erstellt SICK Entry (sofort COMPLETED, 0 Minuten)
 * - 'vacation': Erstellt VACATION Entry (sofort COMPLETED, 0 Minuten)
 */
export async function startDay(roster: RosterEntry[]): Promise<boolean> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return false;
  }

  if (!isEditable.value) {
    showToast('error', 'Zeitmeldung ist bereits abgeschlossen');
    return false;
  }

  // Pruefen ob mindestens ein Mitarbeiter anwesend ist
  const presentEmployees = roster.filter((r) => r.reason === 'present');
  if (presentEmployees.length === 0) {
    showToast('warning', 'Mindestens ein Mitarbeiter muss anwesend sein');
    return false;
  }

  // GPS holen (non-blocking, 5s timeout)
  isCapturingLocation.value = true;
  let location: GeoPoint | undefined;
  try {
    const gps = await captureLocation();
    if (gps) {
      location = { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy };
    }
  } finally {
    isCapturingLocation.value = false;
  }

  const now = getNowTimestamp();
  const entryTime = getNowTimeForEntry(); // Ohne Sekunden für Start/End
  const newEntries: TimeEntry[] = [];

  for (const rosterEntry of roster) {
    // Finde Employee-Daten
    const employee = teamMembers.value.find((e) => e.id === rosterEntry.employeeId);
    if (!employee) {
      console.warn('[Store] Mitarbeiter nicht gefunden:', rosterEntry.employeeId);
      continue;
    }

    // Pruefe ob bereits ein RUNNING Entry fuer diesen Mitarbeiter existiert
    const hasRunning = entries.value.some(
      (e) => e.employeeId === rosterEntry.employeeId && e.status === 'RUNNING'
    );
    if (hasRunning) {
      console.warn('[Store] Mitarbeiter hat bereits laufenden Timer:', employee.firstName, employee.lastName);
      continue;
    }

    let entryType: TimeEntryType;
    let entryStatus: 'RUNNING' | 'COMPLETED';
    let endTime: string | null;
    let note: string | undefined;

    if (rosterEntry.reason === 'present') {
      entryType = 'WORK';
      entryStatus = 'RUNNING';
      endTime = null;
      note = undefined;
    } else if (rosterEntry.reason === 'sick') {
      entryType = 'SICK';
      entryStatus = 'COMPLETED';
      endTime = entryTime; // Sofort abgeschlossen, 0 Minuten Dauer
      note = 'Vom Vorarbeiter als krank gemeldet';
    } else {
      entryType = 'VACATION';
      entryStatus = 'COMPLETED';
      endTime = entryTime;
      note = 'Vom Vorarbeiter als Urlaub gemeldet';
    }

    const entry: TimeEntry = {
      id: createId(),
      timeSheetId: sheet.id,
      employeeId: employee.id,
      personnelNumber: employee.personnelNumber,
      employeeName: `${employee.firstName} ${employee.lastName}`,
      type: entryType,
      status: entryStatus,
      startTime: entryTime,
      endTime,
      breakMinutes: 0,
      startLocation: rosterEntry.reason === 'present' ? location : undefined,
      note,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };

    newEntries.push(entry);
  }

  if (newEntries.length === 0) {
    showToast('error', 'Keine Einträge erstellt');
    return false;
  }

  // Alle Entries in DB speichern
  await db.timeEntry.bulkAdd(newEntries);

  // TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // State aktualisieren
  entries.value = [...entries.value, ...newEntries];
  currentSheet.value = updatedSheet;

  // Roster Selection zuruecksetzen
  rosterSelection.value = new Map();

  const presentCount = newEntries.filter((e) => e.type === 'WORK').length;
  const absentCount = newEntries.filter((e) => e.type !== 'WORK').length;

  let message = `Arbeitstag gestartet (${presentCount} anwesend`;
  if (absentCount > 0) {
    message += `, ${absentCount} abwesend`;
  }
  message += ')';

  showToast('success', message);
  console.info('[Store] Arbeitstag gestartet:', presentCount, 'anwesend,', absentCount, 'abwesend');

  return true;
}

/**
 * Beendet den Arbeitstag - stoppt alle laufenden Timer.
 *
 * 1. Setzt alle RUNNING entries auf COMPLETED
 * 2. Berechnet Pausenzeiten (gesetzliche Regelung)
 * 3. Erfasst GPS-Position
 */
export async function stopDay(): Promise<boolean> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return false;
  }

  const runningList = entries.value.filter((e) => e.status === 'RUNNING');

  if (runningList.length === 0) {
    showToast('warning', 'Keine laufenden Timer zum Stoppen');
    return false;
  }

  // GPS holen (non-blocking, 5s timeout)
  isCapturingLocation.value = true;
  let location: GeoPoint | undefined;
  try {
    const gps = await captureLocation();
    if (gps) {
      location = { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy };
    }
  } finally {
    isCapturingLocation.value = false;
  }

  const now = getNowTimestamp();
  const entryTime = getNowTimeForEntry(); // Ohne Sekunden für endTime
  const updatedEntries: TimeEntry[] = [];

  for (const entry of runningList) {
    // Brutto-Minuten berechnen und dann Pausenzeit ableiten
    const grossMinutes = getMinutesBetween(entry.startTime, entryTime);
    const breakMinutes = calculateStatutoryBreak(grossMinutes);

    const updated: TimeEntry = {
      ...entry,
      status: 'COMPLETED',
      endTime: entryTime,
      endLocation: location,
      breakMinutes,
      updatedAt: now,
      version: entry.version + 1,
    };

    updatedEntries.push(updated);
  }

  // Alle Updates in DB speichern
  await db.timeEntry.bulkPut(updatedEntries);

  // TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // State aktualisieren - bestehende Entries ersetzen
  entries.value = entries.value.map((e) => {
    const updated = updatedEntries.find((u) => u.id === e.id);
    return updated ?? e;
  });
  currentSheet.value = updatedSheet;

  showToast('success', `Arbeitstag beendet (${runningList.length} Einträge)`);
  console.info('[Store] Arbeitstag beendet:', runningList.length, 'Entries gestoppt');

  return true;
}

/**
 * Setzt den Arbeitstag fort - startet neue Timer fuer alle bisherigen WORK-Mitarbeiter.
 *
 * Verwendung: Nach einer Pause (alle Timer gestoppt) kann der Vorarbeiter
 * mit einem Klick alle wieder starten.
 *
 * Erstellt NEUE Entries (fuer korrekte Pausenberechnung pro Session).
 */
export async function resumeDay(): Promise<boolean> {
  const sheet = currentSheet.value;

  if (!sheet) {
    showToast('error', 'Kein TimeSheet geladen');
    return false;
  }

  if (!isEditable.value) {
    showToast('error', 'Zeitmeldung ist bereits abgeschlossen');
    return false;
  }

  // Finde alle bisherigen WORK-Mitarbeiter (COMPLETED)
  const previousWorkEntries = entries.value.filter(
    (e) => e.type === 'WORK' && e.status === 'COMPLETED'
  );

  // Unique Mitarbeiter (falls mehrere Sessions)
  const uniqueEmployeeIds = new Set(previousWorkEntries.map((e) => e.employeeId));

  if (uniqueEmployeeIds.size === 0) {
    showToast('warning', 'Keine Mitarbeiter zum Fortsetzen gefunden');
    return false;
  }

  // GPS holen (non-blocking, 5s timeout)
  isCapturingLocation.value = true;
  let location: GeoPoint | undefined;
  try {
    const gps = await captureLocation();
    if (gps) {
      location = { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy };
    }
  } finally {
    isCapturingLocation.value = false;
  }

  const now = getNowTimestamp();
  const entryTime = getNowTimeForEntry(); // Ohne Sekunden für startTime
  const newEntries: TimeEntry[] = [];

  for (const employeeId of uniqueEmployeeIds) {
    // Finde die letzte Entry dieses Mitarbeiters fuer Metadaten
    const lastEntry = previousWorkEntries
      .filter((e) => e.employeeId === employeeId)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];

    if (!lastEntry) continue;

    // Pruefe ob bereits ein RUNNING Entry fuer diesen Mitarbeiter existiert
    const hasRunning = entries.value.some(
      (e) => e.employeeId === employeeId && e.status === 'RUNNING'
    );
    if (hasRunning) {
      console.info('[Store] Mitarbeiter hat bereits laufenden Timer:', lastEntry.employeeName);
      continue;
    }

    const entry: TimeEntry = {
      id: createId(),
      timeSheetId: sheet.id,
      employeeId: lastEntry.employeeId,
      personnelNumber: lastEntry.personnelNumber,
      employeeName: lastEntry.employeeName,
      type: 'WORK',
      status: 'RUNNING',
      startTime: entryTime,
      endTime: null,
      breakMinutes: 0,
      startLocation: location,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };

    newEntries.push(entry);
  }

  if (newEntries.length === 0) {
    showToast('info', 'Alle Timer laufen bereits');
    return false;
  }

  // Alle Entries in DB speichern
  await db.timeEntry.bulkAdd(newEntries);

  // TimeSheet updatedAt aktualisieren
  const updatedSheet: TimeSheet = {
    ...sheet,
    updatedAt: now,
  };
  await db.timeSheet.put(updatedSheet);

  // State aktualisieren
  entries.value = [...entries.value, ...newEntries];
  currentSheet.value = updatedSheet;

  showToast('success', `Arbeit fortgesetzt (${newEntries.length} Mitarbeiter)`);
  console.info('[Store] Arbeit fortgesetzt:', newEntries.length, 'Timer gestartet');

  return true;
}

/**
 * Aktualisiert den Roster-Selection State.
 * Wird von RosterSelector-Komponente verwendet.
 */
export function setRosterSelection(employeeId: string, reason: AbsenceReason): void {
  const newSelection = new Map(rosterSelection.value);
  newSelection.set(employeeId, reason);
  rosterSelection.value = newSelection;
}

/**
 * Gibt die Roster-Auswahl als RosterEntry-Array zurueck.
 */
export function getRosterEntries(): RosterEntry[] {
  return Array.from(rosterSelection.value.entries()).map(([employeeId, reason]) => ({
    employeeId,
    reason,
  }));
}

// =============================================================================
// Reset
// =============================================================================

/**
 * Setzt den Store zurueck (z.B. bei Logout).
 */
export function resetStore(): void {
  cancelAutoRetry();
  currentSheet.value = null;
  entries.value = [];
  teamMembers.value = [];
  config.value = null;
  isInitialized.value = false;
  syncStatus.value = 'idle';
  lastSyncError.value = null;
  isCapturingLocation.value = false;
  nextRetryAt.value = null;
  retryCount.value = 0;
  rosterSelection.value = new Map();
}

// =============================================================================
// Debug Helpers (nur Development)
// =============================================================================

/**
 * Gibt den aktuellen Store-Zustand in der Console aus.
 */
export function debugStore(): void {
  console.group('[Store Debug]');
  console.info('isInitialized:', isInitialized.value);
  console.info('isLoading:', isLoading.value);
  console.info('syncStatus:', syncStatus.value);
  console.info('lastSyncError:', lastSyncError.value);
  console.info('isCapturingLocation:', isCapturingLocation.value);
  console.info('currentSheet:', currentSheet.value);
  console.info('entries:', entries.value);
  console.info('teamMembers:', teamMembers.value);
  console.info('config:', config.value);
  console.groupEnd();
}

/**
 * [DEBUG] Entsperrt das aktuelle TimeSheet.
 * NUR für Development/Demo!
 */
export async function unlockSheet(): Promise<boolean> {
  const sheet = currentSheet.value;

  if (!sheet) {
    console.warn('[Debug] Kein TimeSheet geladen');
    return false;
  }

  if (!sheet.taLocked && sheet.status === 'DRAFT') {
    console.info('[Debug] TimeSheet ist bereits entsperrt');
    return true;
  }

  // Laufende Retries abbrechen
  cancelAutoRetry();

  const now = getNowTimestamp();

  const unlocked: TimeSheet = {
    ...sheet,
    taLocked: false,
    status: 'DRAFT',
    submissionLocation: undefined,
    updatedAt: now,
    version: sheet.version + 1,
  };

  // In DB speichern
  await db.timeSheet.put(unlocked);

  // State zuruecksetzen
  currentSheet.value = unlocked;
  syncStatus.value = 'idle';
  lastSyncError.value = null;
  nextRetryAt.value = null;
  retryCount.value = 0;

  console.info('[Debug] TimeSheet entsperrt:', sheet.id);
  showToast('info', '[DEBUG] TimeSheet entsperrt');
  return true;
}

// Export for debugging in browser console (nur Development)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const win = window as unknown as Record<string, unknown>;
  win.debugTimeSheetStore = debugStore;
  win.unlockTimeSheet = unlockSheet;
  console.info('[Dev] Debug-Funktionen verfügbar: debugTimeSheetStore(), unlockTimeSheet()');
}
